// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Tooltip, Typography,
} from '@mui/material';
import UsbIcon from '@mui/icons-material/Usb';
import { dateTime, dateUtc } from '../../utils/dates';
import { DETECTOR_STATES } from '../../utils/detecteur';

// Même fenêtre que le backend (detector_health._KIT_ONLINE_WINDOW) : un
// serveur est en ligne si son /hello ou son rapport de santé date de moins de
// dix minutes.
const ONLINE_MINUTES = 10;

function serverOnline(s) {
  // `box_online` est calculé par le backend, et recalculé ici seulement s'il
  // manque (route miroir qui ne le rend pas, ou charge réduite).
  if (typeof s?.detector?.box_online === 'boolean') return s.detector.box_online;
  const d = dateUtc(s?.last_seen);
  return !!d && Date.now() - d.getTime() < ONLINE_MINUTES * 60 * 1000;
}

/**
 * Serveurs d'un établissement : présence, connexion, état de la détection. Un
 * détecteur `crashed` sort en encart rouge nommant le serveur et la raison.
 * L'API compte des caméras (`cameras_total`, `detector.cameras_configured`),
 * pas des chambres : une caméra dessert une chambre (`KitCamera.property_id`),
 * mais la couverture n'est pas mesurée ici.
 */
export default function ServersBlock({ site, etat: state, onPreparerCle: onPrepareKey }) {
  const items = state?.liste || [];
  const broken = items.filter((s) => (s.detector?.detector_state || '') === 'crashed');
  const configured = items.reduce((n, s) => n + (s.detector?.cameras_configured || 0), 0);
  const reachable = items.reduce((n, s) => n + (s.detector?.cameras_reachable || 0), 0);
  const rooms = site.rooms ?? 0;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">
          {site.name}
          {site.city ? <Typography component="span" variant="caption" color="text.secondary"> · {site.city}</Typography> : null}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<UsbIcon />}
          disabled={site.is_active === false}
          onClick={() => onPrepareKey(site.id)}>
          Préparer une clé
        </Button>
      </Box>

      {/* `!state` signifie « pas encore chargé », pas « aucun serveur » : un
          établissement rattaché pendant que la fiche est ouverte n'a pas
          encore de réponse. */}
      {!state || state.chargement ? (
        <CircularProgress size={18} />
      ) : state?.erreur ? (
        <Alert severity="error">{state.erreur}</Alert>
      ) : state?.routeAbsente ? (
        <Typography variant="caption" color="text.secondary">
          État du serveur illisible depuis le back-office : la route
          <code> GET /api/pro/admin/sites/{'{site_id}'}/servers </code>
          n&apos;a rien répondu à ce compte. Elle exige la permission « modifier les
          clients » ou « gérer l&apos;équipe ».
        </Typography>
      ) : items.length === 0 ? (
        <Alert severity="info">
          Aucun serveur enrôlé sur cet établissement. Préparez une clé de pose :
          le serveur apparaîtra ici dès son premier bonjour à l&apos;API.
        </Alert>
      ) : (
        <>
          {broken.length > 0 && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              <strong>
                {broken.length === 1 ? 'La détection est tombée' : `${broken.length} détecteurs sont tombés`}
                {' '}sur cet établissement.
              </strong>
              {' '}
              {broken.map((s) => (
                `${s.code}${s.detector?.detector_reason ? ` (${s.detector.detector_reason})` : ''}`
              )).join(', ')}
              {' : '}les chambres servies par {broken.length === 1 ? 'ce serveur' : 'ces serveurs'} ne
              sont plus surveillées. Une box qui répond ne dit rien de sa
              détection : c&apos;est cet encart qui le dit.
            </Alert>
          )}

          {items.map((s) => {
            const enLigne = serverOnline(s);
            const detectorState = DETECTOR_STATES[s.detector?.detector_state]
              || DETECTOR_STATES.unknown;
            return (
              <Box key={s.kit_id || s.code} sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Tooltip title={enLigne ? 'Vu il y a moins de dix minutes' : `Dernier contact : ${dateTime(s.last_seen, 'jamais')}`}>
                    <Box sx={{
                      width: 10, height: 10, borderRadius: '50%',
                      bgcolor: enLigne ? 'success.main' : 'text.disabled',
                    }} />
                  </Tooltip>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {s.code}
                  </Typography>
                  <Chip size="small" label={detectorState.label} color={detectorState.couleur} />
                  {!enLigne && (
                    <Typography variant="caption" color="text.secondary">
                      hors ligne · vu {dateTime(s.last_seen, 'jamais')}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {s.cameras_total ?? 0} caméra(s) rattachée(s)
                  {' · '}{s.detector?.cameras_configured ?? 0} configurée(s)
                  {' · '}{s.detector?.cameras_reachable ?? 0} joignable(s)
                  {s.mise_a_jour
                    ? ((s.mise_a_jour.version_en_service || s.mise_a_jour.version_posee)
                      ? ` · version ${s.mise_a_jour.version_en_service || s.mise_a_jour.version_posee}`
                      : '')
                    : (s.agent_version ? ` · agent ${s.agent_version}` : '')}
                  {s.detector?.restarts ? ` · ${s.detector.restarts} redémarrage(s)` : ''}
                </Typography>
                {s.detector?.detector_reason && s.detector.detector_state !== 'running' && (
                  <Typography variant="caption" color="error.main" display="block">
                    {s.detector.detector_reason}
                    {s.detector.state_since ? ` (depuis le ${dateTime(s.detector.state_since, 'jamais')})` : ''}
                  </Typography>
                )}
                {s.mise_a_jour?.echec && (
                  <Typography variant="caption" color="warning.main" display="block">
                    Mise à jour : {s.mise_a_jour.echec.libelle}
                    {s.mise_a_jour.echec.le ? ` (le ${dateTime(s.mise_a_jour.echec.le, 'jamais')})` : ''}
                  </Typography>
                )}
              </Box>
            );
          })}

          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 1 }}
            color={rooms > 0 && configured < rooms ? 'warning.main' : 'text.secondary'}
          >
            {rooms} chambre(s) déclarée(s) sur l&apos;établissement, {configured} caméra(s)
            configurée(s) côté détecteur, {reachable} joignable(s). Une caméra dessert
            une chambre : ces nombres se comparent, ils ne se confondent pas.
          </Typography>
        </>
      )}
    </Paper>
  );
}
